import { portalPaths } from '@provision/portal-routes';
import { NavigationDataItem } from 'src/components/Navbar/use-navigation-items';
import { PartnerGroupRules, UserGroupRules } from 'src/graphql';

export interface SettingsNavigationGroup {
  id: string;
  title: string;
  items: NavigationDataItem[];
}

const withDefaults = (
  items: (Omit<NavigationDataItem, 'id'> & { id?: string })[],
): NavigationDataItem[] =>
  items.map((item) => ({
    ...item,
    id: item.id ?? item.path,
    iconData: { library: 'lucide', ...item.iconData },
    isMultitenancyAllowed: false,
    isMobileSupported: item.isMobileSupported ?? false,
  }));

export const SETTINGS_NAVIGATION_GROUPS: SettingsNavigationGroup[] = [
  {
    id: 'settings-admin',
    title: 'Admin',
    items: withDefaults([
      {
        title: 'Company',
        iconData: { name: 'building' },
        path: portalPaths.settings.companyInfo(),
        isMobileSupported: true,
      },
      {
        title: 'Users',
        iconData: { name: 'users' },
        path: portalPaths.settings.users(),
        allowedRules: [UserGroupRules.AccessToUserManagementV2],
      },
      {
        title: 'Memberships',
        iconData: { name: 'id-card' },
        path: portalPaths.settings.memberships(),
        allowedRules: [
          {
            rules: [
              PartnerGroupRules.AccessToSubscriptionManagement,
              UserGroupRules.AccessToBilling,
            ],
          },
          {
            rules: [
              PartnerGroupRules.AccessToSubscriptionManagement,
              UserGroupRules.AccessToViewMembershipsSettingsPage,
            ],
          },
        ],
      },
      {
        title: 'Stripe',
        iconData: { name: 'credit-card' },
        path: portalPaths.settings.stripe(),
        allowedRules: [
          PartnerGroupRules.AccessToSubscriptionManagement,
          UserGroupRules.AccessToBilling,
        ],
        matchType: 'all' as const,
      },
      {
        title: 'Notifications',
        iconData: { name: 'bell' },
        path: portalPaths.settingsNotifications(),
        allowedRules: [
          UserGroupRules.AccessToNotificationPreferences,
          UserGroupRules.AccessToViewConversations,
        ],
        matchType: 'all' as const,
        isMobileSupported: true,
      },
      {
        title: 'Data Import',
        iconData: { name: 'upload' },
        path: portalPaths.settings.dataImport(),
        allowedRules: [UserGroupRules.AccessToDataImport],
      },
    ]),
  },
  {
    id: 'settings-platform',
    title: 'Platform',
    items: withDefaults([
      {
        title: 'Channels',
        iconData: { name: 'waypoints' },
        path: portalPaths.settings.channels(),
        allowedRules: [UserGroupRules.AccessToManageChannelSettings],
      },
      {
        title: 'Integrations',
        iconData: { name: 'plug' },
        path: portalPaths.settings.integrations(),
        allowedRules: [UserGroupRules.AccessToSettingsIntegrations],
      },
      {
        title: 'Preferences',
        iconData: { name: 'sliders-horizontal' },
        path: portalPaths.settings.preferences(),
        allowedRules: [UserGroupRules.AccessToSettingsPreferences],
        isMobileSupported: true,
      },
    ]),
  },
  {
    id: 'settings-workflow',
    title: 'Workflow',
    items: withDefaults([
      {
        title: 'Rules',
        iconData: { name: 'list-checks' },
        path: portalPaths.settings.rulesEngine(),
        allowedRules: [UserGroupRules.AccessToRulesEngine],
      },
      {
        title: 'Workflows',
        iconData: { name: 'workflow' },
        path: portalPaths.settings.workflows(),
        allowedRules: [
          PartnerGroupRules.AccessToManageProvisionWorkflows,
          UserGroupRules.AccessToManageProvisionWorkflows,
        ],
      },
      {
        title: 'Macros',
        iconData: { name: 'zap' },
        path: portalPaths.settings.macros(),
        allowedRules: [UserGroupRules.AccessToManageMacros],
      },
      {
        title: 'Labels',
        iconData: { name: 'tags' },
        path: portalPaths.settings.manageLabels(),
        allowedRules: [UserGroupRules.AccessToManageLabels],
      },
      {
        title: 'Custom Properties',
        iconData: { name: 'braces' },
        path: portalPaths.settings.manageCustomAttributes(),
        allowedRules: [UserGroupRules.AccessToManageCustomAttributes],
      },
      {
        title: 'Business Hours',
        iconData: { name: 'clock' },
        path: portalPaths.settings.businessHours(),
        allowedRules: [UserGroupRules.AccessToManageBusinessHours],
      },
      {
        title: 'Holiday Schedule',
        iconData: { name: 'calendar' },
        path: portalPaths.settings.holidaySchedule(),
        isMobileSupported: true,
      },
      {
        title: 'Signal Schedule',
        iconData: { name: 'radio-tower' },
        path: portalPaths.settings.signal(),
        allowedRules: [PartnerGroupRules.ProvisionSignal, UserGroupRules.ProvisionSignal],
        isMobileSupported: true,
      },
      {
        title: 'Escalation Windows',
        iconData: { name: 'alarm-clock' },
        path: portalPaths.settings.signalBusinessHours(),
        allowedRules: [UserGroupRules.AccessToManageEscalationSettings],
      },
      {
        title: 'Blocked Contacts',
        iconData: { name: 'ban' },
        path: portalPaths.settings.blockedContacts(),
        allowedRules: [UserGroupRules.AccessToManageBlockedContacts],
      },
    ]),
  },
];

export const SETTINGS_NAVIGATION: NavigationDataItem[] = SETTINGS_NAVIGATION_GROUPS.flatMap(
  (group) => group.items,
);
